<template>
  <div class="w-full">
    <slot :error="error" :touched="validations?.$dirty" :any-error="validations?.anyError" />
    <slot name="error" v-if="error && showMessage">
      <div class="flex items-center mt-1" v-hubble="'error-wrapper'">
        <PulseLineExclamationCircle class="h-[14px] w-[14px] mr-1 text-danger-700 fill-current" />
        <span class="text-xs font-medium text-danger-700">{{ errorsToShow[0] }}</span>
      </div>
    </slot>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, computed, inject } from 'vue';
import { get } from '../../utils/accessors';
import translateErr from '../../utils/errors';
import translate from '../../utils/translate';

const language = inject('language', 'en');
const errorMessages = translate(language, 'errors');
const commonValidators = Object.keys(errorMessages);

type validatorName = keyof typeof errorMessages;

type ValidationGroups = {
  [key in validatorName]: any;
};

interface ValidationObject extends ValidationGroups {
  $anyError: boolean;
}

const props = defineProps({
  field: {
    type: String as PropType<string>,
    required: true,
  },
  requestErrors: {
    type: Array as PropType<string[] | { field: string; error: string }[]>,
    required: false,
    default: () => [],
  },
  showMessage: {
    type: Boolean,
    required: false,
    default: () => true,
  },
});

const validations = inject('validations') as any;
const fieldValidations = computed(() => {
  return get(validations?.value, props.field) as ValidationObject;
});

function createErrorMessage(opts: { validator: validatorName; values: Record<string, string | number> }): string {
  const { validator, values } = opts;

  return translateErr(language, String(validator), {
    field: props.field,
    ...get(fieldValidations.value, [String(validator), '$params'], {}),
    ...values,
  });
}

const errorsToShow = computed(() => {
  if (props.requestErrors.length) {
    if (props.requestErrors.every(error => typeof error === 'string')) {
      return props.requestErrors;
    }

    const fieldHasError = props.requestErrors.find(error => error.field === props.field);

    if (fieldHasError) {
      return [fieldHasError.error];
    }
  }

  if (!fieldValidations?.value || !fieldValidations?.value?.$invalid) {
    return [];
  }

  return commonValidators.reduce((result: string[], commonValidator: string) => {
    if (fieldValidations?.value[commonValidator as any] && fieldValidations?.value[commonValidator as any]?.$invalid) {
      result.push(
        createErrorMessage({
          validator: commonValidator as validatorName,
          values: {},
        }),
      );
    }

    return result;
  }, []);
});

const error = computed(() => {
  return (validations?.value.$dirty && fieldValidations?.value.$invalid) || props.requestErrors.length > 0;
});
</script>
